import { useState, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';
import detectRegion from '../utils/detectRegion';

/**
 * useEmpathicHandover - React hook for the Empathetic AI-to-Human Handover System
 *
 * Integrates CEOE + GNEP + NLHID pipeline passively into chat.
 * When handover is triggered, exposes consent flow and session management.
 */
const useEmpathicHandover = () => {
  const [handoverState, setHandoverState] = useState({
    active: false,
    nudgeLevel: 0,
    nudgeText: null,
    crisisScore: 0,
    handoverTriggered: false,
    emergencyOverride: false,
    sentimentTrend: 'stable',
    showConsent: false,
    connecting: false,
    session: null,
    farewell: null,
    expert: null,
    crisisContacts: null,
    error: null
  });

  const lastAnalysis = useRef(null);

  /**
   * Analyze a user message through the CEOE → GNEP → NLHID pipeline
   * Call this on every chat message send
   */
  const analyzeMessage = useCallback(async (message, avaName = 'AvA', sessionId = null) => {
    try {
      const result = await apiClient.request('/api/empathic-handover/analyze', {
        method: 'POST',
        body: JSON.stringify({ message, ava_name: avaName, session_id: sessionId })
      });

      lastAnalysis.current = result;

      setHandoverState(prev => ({
        ...prev,
        nudgeLevel: result.nudge_level,
        nudgeText: result.nudge_text,
        crisisScore: result.crisis_score,
        handoverTriggered: result.handover_triggered,
        emergencyOverride: result.emergency_override,
        sentimentTrend: result.sentiment_trend,
        showConsent: result.handover_triggered,
        active: result.nudge_level > 0 || result.handover_triggered
      }));

      return result;
    } catch (err) {
      console.error('Empathic handover analysis failed:', err);
      return null;
    }
  }, []);

  /**
   * Initiate the handover with user consent
   */
  const initiateHandover = useCallback(async (consent = {}) => {
    setHandoverState(prev => ({ ...prev, connecting: true, error: null }));

    try {
      const result = await apiClient.request('/api/empathic-handover/initiate', {
        method: 'POST',
        body: JSON.stringify({
          confirm_intent: consent.confirmIntent !== false,
          share_first_name: consent.shareFirstName !== false,
          reviewed_pre_brief: consent.reviewedPreBrief || false,
          preferred_channel: consent.preferredChannel || 'chat',
          region: consent.region || detectRegion()
        })
      });

      if (result.status === 'declined') {
        setHandoverState(prev => ({
          ...prev,
          showConsent: false,
          connecting: false,
          handoverTriggered: false,
          nudgeLevel: 0,
          nudgeText: null
        }));
        return result;
      }

      setHandoverState(prev => ({
        ...prev,
        connecting: false,
        showConsent: false,
        session: result.session,
        farewell: result.farewell_message,
        expert: result.expert,
        interventionId: result.intervention_id,
        avaMessage: result.ava_message,
        primaryReferral: result.primary_referral,
        secondaryReferral: result.secondary_referral,
        internationalDirectory: result.international_directory
      }));

      return result;
    } catch (err) {
      setHandoverState(prev => ({
        ...prev,
        connecting: false,
        error: 'Failed to connect. Please try calling the helpline directly.'
      }));
      return null;
    }
  }, []);

  /**
   * Decline the handover
   */
  const declineHandover = useCallback(async () => {
    let crisisContacts = null;
    try {
      const res = await apiClient.request('/api/empathic-handover/initiate', {
        method: 'POST',
        body: JSON.stringify({ confirm_intent: false })
      });
      // A declined CRITICAL still surfaces crisis contacts in the chat
      if (res && res.crisis_contacts) {
        crisisContacts = { contacts: res.crisis_contacts, message: res.message };
      }
    } catch (err) {
      // Silent fail
    }
    setHandoverState(prev => ({
      ...prev,
      showConsent: false,
      handoverTriggered: false,
      nudgeLevel: 0,
      nudgeText: null,
      crisisContacts
    }));
  }, []);

  const dismissCrisisContacts = useCallback(() => {
    setHandoverState(prev => ({ ...prev, crisisContacts: null }));
  }, []);

  /**
   * Dismiss nudge text (after it's been shown in chat)
   */
  const dismissNudge = useCallback(() => {
    setHandoverState(prev => ({ ...prev, nudgeText: null }));
  }, []);

  /**
   * Force-open the 3-step consent modal in response to a blocking signal
   * from /api/hibff-chat (triggerHandover=true). Guarantees the single
   * handover UX fires on every blocking path — input safety filter,
   * snippet output rail, NeMo output rail, classifier-blocked output —
   * even if the parallel /api/empathic-handover/analyze call hasn't yet
   * returned or returned handover_triggered=false.
   */
  const forceOpenConsent = useCallback(() => {
    setHandoverState(prev => ({
      ...prev,
      showConsent: true,
      handoverTriggered: true,
      active: true,
    }));
  }, []);

  /**
   * Reset state after session ends
   */
  const resetState = useCallback(() => {
    setHandoverState({
      active: false,
      nudgeLevel: 0,
      nudgeText: null,
      crisisScore: 0,
      handoverTriggered: false,
      emergencyOverride: false,
      sentimentTrend: 'stable',
      showConsent: false,
      connecting: false,
      session: null,
      farewell: null,
      expert: null,
      interventionId: null,
      crisisContacts: null,
      error: null
    });
  }, []);

  return {
    ...handoverState,
    analyzeMessage,
    initiateHandover,
    declineHandover,
    dismissNudge,
    dismissCrisisContacts,
    forceOpenConsent,
    resetState
  };
};

export default useEmpathicHandover;
